/**
 * Shared utilities for SortingCanvasComponent and its configuration form
 * This ensures consistency between form configuration and component rendering
 */

// Available object types/shapes
export const AVAILABLE_SHAPES = [
  'circle',
  'square', 
  'triangle',
  'star',
  'diamond',
  'hexagon',
  'heart',
  'cross',
  'moon',
  'sun'
] as const;

export type ShapeType = typeof AVAILABLE_SHAPES[number];

// Available colors
export const AVAILABLE_COLORS = [
  'red',
  'blue',
  'green',
  'yellow',
  'purple',
  'orange',
  'pink',
  'brown',
  'black',
  'white',
  'gray',
  'cyan',
  'magenta',
  'lime',
  'teal'
] as const;

export type ColorType = typeof AVAILABLE_COLORS[number];

// Available sizes
export const AVAILABLE_SIZES = [
  'tiny',
  'small',
  'medium',
  'large',
  'huge'
] as const;

export type SizeType = typeof AVAILABLE_SIZES[number];

// Color value mapping (hex codes)
export const COLOR_VALUES: Record<ColorType, string> = {
  'red': '#EF4444',
  'blue': '#3B82F6',
  'green': '#10B981',
  'yellow': '#F59E0B',
  'purple': '#8B5CF6',
  'orange': '#F97316',
  'pink': '#EC4899',
  'brown': '#A0522D',
  'black': '#000000',
  'white': '#FFFFFF',
  'gray': '#6B7280',
  'cyan': '#06B6D4',
  'magenta': '#D946EF',
  'lime': '#84CC16',
  'teal': '#14B8A6'
};

// Shape icon mapping (Unicode symbols)
export const SHAPE_ICONS: Record<ShapeType, string> = {
  'circle': '●',
  'square': '■',
  'triangle': '▲',
  'star': '⭐',
  'diamond': '◆',
  'hexagon': '⬡',
  'heart': '❤️',
  'cross': '✚',
  'moon': '🌙',
  'sun': '☀️'
};

// Size value mapping (pixel dimensions)
export const SIZE_VALUES: Record<SizeType, number> = {
  'tiny': 16,
  'small': 20,
  'medium': 24,
  'large': 32,
  'huge': 40
};

// Utility functions
export const getColorValue = (colorName: string): string => {
  return COLOR_VALUES[colorName as ColorType] || colorName;
};

export const getShapeIcon = (shape: string): string => {
  return SHAPE_ICONS[shape as ShapeType] || '●';
};

export const getSizeValue = (size: string): number => {
  return SIZE_VALUES[size as SizeType] || SIZE_VALUES.medium;
};

// Validation functions
export const isValidShape = (shape: string): shape is ShapeType => {
  return AVAILABLE_SHAPES.includes(shape as ShapeType);
};

export const isValidColor = (color: string): color is ColorType => {
  return AVAILABLE_COLORS.includes(color as ColorType);
};

export const isValidSize = (size: string): size is SizeType => {
  return AVAILABLE_SIZES.includes(size as SizeType);
};

// Default configurations
export const DEFAULT_OBJECT_CONFIG = {
  type: 'circle' as ShapeType,
  count: 1,
  color: 'red' as ColorType,
  size: 'medium' as SizeType
};

export const DEFAULT_CATEGORY_CONFIG = {
  id: '',
  name: 'Nouvelle catégorie',
  color: '#3B82F6',
  sortingCriteria: 'custom' as const,
  availableValues: [] as string[]
};

// Helper function to get available values for a specific criteria
export const getAvailableValuesForCriteria = (
  criteria: string, 
  objects: Array<{ type: string; color: string; size?: string }>
): string[] => {
  switch (criteria) {
    case 'color':
      return Array.from(new Set(objects.map(obj => obj.color)));
    case 'shape':
      return Array.from(new Set(objects.map(obj => obj.type)));
    case 'size':
      return Array.from(new Set(objects.map(obj => obj.size || 'medium').filter(Boolean)));
    default:
      return [];
  }
};

// Helper function to ensure all categories have required fields
export const ensureCategoryFields = (category: any) => ({
  ...category,
  availableValues: category.availableValues || []
});

// Helper function to render object preview
export const renderObjectPreview = (
  type: string, 
  color: string, 
  size: string = 'medium'
) => {
  const sizeValue = getSizeValue(size);
  const colorValue = getColorValue(color);
  const shapeIcon = getShapeIcon(type);
  
  return {
    sizeValue,
    colorValue,
    shapeIcon,
    style: {
      width: sizeValue,
      height: sizeValue,
      backgroundColor: colorValue,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: Math.max(sizeValue * 0.6, 12),
      color: colorValue === '#FFFFFF' ? '#000000' : '#FFFFFF',
      border: colorValue === '#FFFFFF' ? '1px solid #D1D5DB' : 'none',
      borderRadius: type === 'square' ? '2px' : '50%'
    }
  };
};

